import { inject, Injectable } from '@angular/core';

import { MijnOmgevingTemplates, MijnOmgevingTemplateService } from '@ap/mijn-template';
import { NativeSelectedAccount, NativeSelectedDebitCard, NativeSignResult } from '@ap/web-container';
import { SessiebeheerResource, SessionStatus } from '@ap/interface-apic-sessiebeheer';
import { ApFileDownloadService } from '@ap/file-download';
import { ApPersistenceService } from '@ap/persistence';
import { ApModalService, ModalClosedReason, ModalOptions, ModalSize } from '@ap/modal';
import { ApContentService } from '@ap/content';
import { MijnWebcomponentIdentifier } from '@ap/mijn-navigatie';
import { WebpartMessagingService } from '@ap/mijn-webpart-messaging';

import content from '../mijn-omgeving.content.json';

import { SignComponent } from './sign/sign.component';
import { SignOptions } from './sign/sign.model';

@Injectable({
    providedIn: 'root'
})
export class MijnOmgevingWebpartMessagingService extends WebpartMessagingService {
    private readonly mijnOmgevingTemplateService = inject(MijnOmgevingTemplateService);
    private readonly modalService = inject(ApModalService);
    private readonly persistenceService = inject(ApPersistenceService);
    private readonly sessiebeheerResource = inject(SessiebeheerResource);
    private readonly apContentService = inject(ApContentService);

    constructor() {
        super();
        this.apContentService.addContentItems(content);
    }

    protected override onSetTitleWeb(identifier: string, payload: string): void {
        this.mijnOmgevingTemplateService.setTemplateSettings({
            title: payload,
            ...(payload && { tabTitle: payload }),
            setTemplate: MijnOmgevingTemplates.ACTIE,
            closeTemplate: this.onClose.bind(this),
            dynamicRouting: true,
            webcomponentIdentifier: identifier as MijnWebcomponentIdentifier
        });
    }

    protected override onSignNativeWeb(identifier: string, payload: any): void {
        const modalOptions: ModalOptions<SignOptions> = new ModalOptions({ actionId: payload.actionId });
        modalOptions.size = ModalSize.LARGE;
        modalOptions.title = this.apContentService.get('mijn.omgeving.modal.sign.title');

        const modalComponent = this.modalService.open(SignComponent, modalOptions);
        modalComponent.afterClosed().subscribe((result) => {
            if (result.reason === ModalClosedReason.ACCEPTED) {
                payload.response({ result: NativeSignResult.SIGNING_DONE });
            } else {
                payload.response({ result: NativeSignResult.SIGNING_CANCELLED });
            }
        });
    }

    protected override onDownloadWeb(identifier: string, payload: any): void {
        ApFileDownloadService.initiateBrowserDownload(payload.filename, payload.blob, payload.blob.type);
    }

    protected override onGetSelectedAccountWeb(identifier: string, payload: any): void {
        const selectedAccount: NativeSelectedAccount = this.persistenceService.getSession('webpart-messaging', 'selectedAccount');
        if (payload?.response && typeof payload.response === 'function') {
            payload.response(selectedAccount);
        }
    }

    protected override onGetSelectedDebitCardWeb(identifier: string, payload: any): void {
        const selectedDebitcard: NativeSelectedDebitCard = this.persistenceService.getSession('webpart-messaging', 'selectedDebitcard');
        if (payload?.response && typeof payload.response === 'function') {
            payload.response(selectedDebitcard);
        }
    }

    protected override onGetSelectedIdentificationWeb(identifier: string, payload: any): void {
        const selectedIdentification = this.persistenceService.getSession('webpart-messaging', 'selectedIdentification');
        if (payload?.response && typeof payload.response === 'function') {
            payload.response(selectedIdentification);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override onLogOutWeb(identifier: string = '', payload: any = null): void {
        this.sessiebeheerResource.patchSession('0', { status: SessionStatus.TERMINATED }).subscribe(() => {
            window.history.go(-1);
        });
    }

    protected override getQueryParams(identifier: string): Record<string, string> {
        const sessionParams = this.persistenceService.getSession(identifier, 'queryParams') as Record<string, string>;
        if (sessionParams && Object.keys(sessionParams).length > 0) {
            return sessionParams;
        }
        return super.getQueryParams(identifier);
    }
}
